import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableSchedule,
  RequestScheduleInterFace,
} from '../../interface/schedule-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { removeDevicePointStatus } from '../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import {
  fetchAllSchedules,
  setScheduleFilterData,
  updateScheduleFilters,
  fetchListFilters,
  setSortData,
} from '../../redux/slice/ScheduleManagementSlice';
import { setSelectedStation } from '../../redux/slice/StationManagementSidePanelSlice';
import { RootState } from '../../redux/store';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import EndConsumerSidePanel from '../../shared-components/table-side-panel/EndConsumerSidePanel';
import StationViewSidePanel from '../../shared-components/table-side-panel/StationViewSidePanel';
import TopHeaderButtons from '../../shared-components/top-header-buttons/TopHeaderButtons';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import { CapitalizeFirstLetter } from '../../shared-functions/StringFunctions';
import scheduleApi from '../../utils/api/schedule';

import DeviceFilter from './components/DeviceFilter';
import ScheduleAvailability from './components/ScheduleAvailability';

/**
 * Schedule management component
 */
function ScheduleManagement() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('scheduleManagement');
  const navigate = useNavigate();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [modalView, setModalView] = useState<{
    status: boolean;
    title: string;
    content: JSX.Element;
    header: boolean;
  }>({
    status: false,
    title: '',
    content: <div />,
    header: false,
  });
  const {
    schedules,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.schedules);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);

  /**
   * Handle the fetch data for Api call function
   * @param data - RequestScheduleInterFace
   */
  const fetchData = (data: RequestScheduleInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllSchedules({
          ...data,
        }),
      );
    }
  };

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingStationDate: AvailableSchedule, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id: chargingStationDate?.chargingStation,
          name: chargingStationDate?.chargingStation,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/schedule-management/station/${chargingStationDate.chargingStation}/station-dashboard?searchBy=${chargingStationDate.chargingStation}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/schedule-management/station/${chargingStationDate.chargingStation}/station-dashboard`,
          search: `?searchBy=${chargingStationDate.chargingStation}`,
        });
      }
    }
  };

  const column = [
    {
      label: t('bookingDate'),
      accessorKey: 'startDateUtc' as keyof AvailableSchedule,
      sortAllow: true,
    },
    {
      label: t('bookingTime'),
      accessorKey: 'time' as keyof AvailableSchedule,
      sortAllow: true,
      /**
       * Booking time view in table
       * @param schedule - AvailableSchedule
       */
      customData: (currentRow: AvailableSchedule) => (
        <p>
          {FormatUtcString(ConvertUTCtoHHMM(currentRow.startDateUtc))} -{' '}
          {FormatUtcString(ConvertUTCtoHHMM(currentRow.endDateUtc))}
        </p>
      ),
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof AvailableSchedule,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'chargingStationAddress' as keyof AvailableSchedule,
      sortAllow: false,
      /**
       * Location view in table
       * @param schedule - AvailableSchedule
       */
      customData: (schedule: AvailableSchedule) =>
        schedule.chargingStationAddress ? (
          <div className="location-in-schedule-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{schedule.chargingStationAddress}</p>
          </div>
        ) : null,
    },
    {
      label: t('vehicleDetails'),
      accessorKey: 'vehicleModel' as keyof AvailableSchedule,
      sortAllow: false,
    },
    {
      label: t('userName'),
      accessorKey: 'user' as keyof AvailableSchedule,
      sortAllow: false,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('userId'),
      accessorKey: 'userCustomId' as keyof AvailableSchedule,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof AvailableSchedule,
      columnWidth: 60,
      /**
       * S.No view
       * @param schedule - AvailableSchedule
       */
      customData: (schedule: AvailableSchedule) => {
        let status = null;
        switch (schedule.status) {
          case 'confirmed':
            status = 'confirmed';
            break;
          case 'cancelled':
            status = 'cancelled';
            break;
          case 'completed':
            status = 'completed';
            break;
          default:
            status = 'N/A';
        }
        return (
          <div className="status-in-schedule-table">
            <p className={`table-row-each-data ${status}`}>
              {CapitalizeFirstLetter(schedule.status)}
            </p>
          </div>
        );
      },
    },
  ];

  /**
   * Close the modal function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };
  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortField,
      sortBy,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setScheduleFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField === 'time' ? 'startDateUtc' : sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Open the device filter modal function
   */
  const deviceFilterModal = () => {
    setModalView({
      status: true,
      title: '',
      header: false,
      content: <DeviceFilter closeModal={closeModal} />,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('bookingDate'), accessorKey: 'startDateUtc' },
    { label: t('bookingTime'), accessorKey: 'time' },
    { label: t('stationName'), accessorKey: 'chargingStation' },
    { label: t('stationLocation'), accessorKey: 'chargingStationAddress' },
    { label: t('vehicleDetails'), accessorKey: 'vehicleModel' },
    { label: t('userName'), accessorKey: 'user' },
    { label: t('userId'), accessorKey: 'userCustomId' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    scheduleApi
      .fetchSchedulesExport(removeEmptyKeys({ ...queryParameters, sortField, sortBy }))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableSchedule, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'startDateUtc':
                return ConvertUTCtoDate(row.startDateUtc);
              case 'time':
                return `${FormatUtcString(ConvertUTCtoHHMM(row.startDateUtc))} - ${FormatUtcString(ConvertUTCtoHHMM(row.endDateUtc))}`;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'schedule-list.csv');
          } else {
            exportPDF(headers, rows, 'schedule-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('chargingSchedule'),
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="schedule-management-wrap">
      <ModalBox
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        open={modalView.status}
        header={modalView.header}
      />
      <div className="schedule-availability-wrap">
        <TopHeaderButtons
          filterFunction={deviceFilterModal}
          filterClearFunction={() => dispatch(removeDevicePointStatus())}
          sizeChangeButtonRequired={false}
          isFicility={false}
        />
        <ScheduleAvailability />
        <div className="schedule-management-header">
          <PageTitle title={t('schedulingDetails')} />
        </div>
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={schedules}
          currentPage={page}
          tableHeight="70vh"
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateScheduleFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refreshCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
    </div>
  );
}

export default ScheduleManagement;
